import { fetchApi } from './api';
import type {
    MCFilterOptions,
    InvoiceLinesResponse,
    LineOriginResponse,
    CounterCandidatesResponse,
    CreateGroupPayload,
    CreateGroupResponse,
    CorrectionGroup,
    SpecialLotsResponse,
    LotRulesResponse,
    LotRulePreviewResponse,
    LotRuleMatchMode,
    LotOriginStatus,
} from '../types/marginCorrections';

const BASE = '/margin-corrections';

/** Raw fetch that preserves the response envelope's `pagination` block (fetchApi drops it). */
async function fetchWithPagination<T>(url: string): Promise<T> {
    const res = await fetch(`/api/v1${url}`, { headers: { 'Content-Type': 'application/json' } });
    if (res.status === 401) {
        const { redirectToLogin } = await import('../utils/auth-redirect');
        redirectToLogin();
        throw new Error('Session expired');
    }
    const body = await res.json();
    if (!res.ok || !body.success) throw new Error(body.error?.message || 'API error');
    return { ...body.data, pagination: body.pagination } as T;
}

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
    }
    const s = sp.toString();
    return s ? `?${s}` : '';
}

export const marginCorrectionsApi = {
    getFilters(company = 'dit'): Promise<MCFilterOptions> {
        return fetchApi(`${BASE}/filters${qs({ company })}`);
    },

    getInvoiceLines(params: {
        company: string;
        customerID?: string;
        productID?: string;
        dateFrom?: string;
        dateTo?: string;
        search?: string;
        specialLotsOnly?: boolean;
        correctedOnly?: boolean;
        marginThreshold?: number;
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
    }): Promise<InvoiceLinesResponse> {
        return fetchWithPagination(`${BASE}/invoice-lines${qs(params)}`);
    },

    getLineOrigin(company: string, documentID: string): Promise<LineOriginResponse> {
        return fetchApi(`${BASE}/line-origin${qs({ company, documentID })}`);
    },

    getCounterCandidates(params: {
        company: string;
        excludeDocumentID?: string;
        onlySpecial?: boolean;
        productID?: string;
        customerID?: string;
        lotCode?: string;
        dateFrom?: string;
        dateTo?: string;
        search?: string;
        limit?: number;
    }): Promise<CounterCandidatesResponse> {
        return fetchApi(`${BASE}/counter-candidates${qs(params)}`);
    },

    listGroups(params: { company: string; includeCancelled?: boolean; page?: number; limit?: number }): Promise<{ groups: CorrectionGroup[]; pagination: { total: number; totalPages: number; page: number } }> {
        return fetchWithPagination(`${BASE}/groups${qs(params)}`);
    },

    getGroup(id: number): Promise<CorrectionGroup & { audit: Array<{ action: string; comment: string | null; changedBy: string; changedAt: string }> }> {
        return fetchApi(`${BASE}/groups/${id}`);
    },

    createGroup(payload: CreateGroupPayload): Promise<CreateGroupResponse> {
        return fetchApi(`${BASE}/groups`, { method: 'POST', body: JSON.stringify(payload) });
    },

    cancelGroup(id: number, reason: string): Promise<{ groupId: number; isActive: boolean }> {
        return fetchApi(`${BASE}/groups/${id}`, { method: 'DELETE', body: JSON.stringify({ reason }) });
    },

    // Special-price lots
    listSpecialLots(company = 'dit'): Promise<SpecialLotsResponse> {
        return fetchApi(`${BASE}/special-lots${qs({ company })}`);
    },

    saveSpecialLot(payload: {
        id?: number;
        company: string;
        lotCode: string;
        inboundEntryNo?: number | null;
        receiptNo?: string | null;
        intendedCustomerID?: string | null;
        negotiatedUnitCostEUR?: number | null;
        notes?: string | null;
    }): Promise<{ id: number; lotCode: string }> {
        return fetchApi(`${BASE}/special-lots`, { method: 'POST', body: JSON.stringify(payload) });
    },

    deleteSpecialLot(id: number): Promise<{ id: number }> {
        return fetchApi(`${BASE}/special-lots/${id}`, { method: 'DELETE' });
    },

    // Lot rules
    listLotRules(company = 'dit'): Promise<LotRulesResponse> {
        return fetchApi(`${BASE}/lot-rules${qs({ company })}`);
    },

    previewLotRule(payload: { company: string; matchMode: LotRuleMatchMode; pattern: string; effectiveFrom?: string | null }): Promise<LotRulePreviewResponse> {
        return fetchApi(`${BASE}/lot-rules/preview`, { method: 'POST', body: JSON.stringify(payload) });
    },

    saveLotRule(payload: {
        id?: number;
        company: string;
        ruleName: string;
        matchMode: LotRuleMatchMode;
        pattern: string;
        effectiveFrom?: string | null;
        isActive: boolean;
        notes?: string | null;
    }, force = false): Promise<{ id: number; isActive: boolean; forced: boolean }> {
        return fetchApi(`${BASE}/lot-rules${force ? '?force=true' : ''}`, { method: 'POST', body: JSON.stringify(payload) });
    },

    deleteLotRule(id: number): Promise<{ id: number }> {
        return fetchApi(`${BASE}/lot-rules/${id}`, { method: 'DELETE' });
    },

    // Lot origin materialisation
    getLotOriginStatus(company = 'dit'): Promise<LotOriginStatus> {
        return fetchApi(`${BASE}/lot-origin/status${qs({ company })}`);
    },

    refreshLotOrigin(company = 'dit', fromDate?: string | null): Promise<LotOriginStatus> {
        return fetchApi(`${BASE}/lot-origin/refresh`, { method: 'POST', body: JSON.stringify({ company, fromDate }) });
    },
};
